/** Generic API response wrapper */
export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  timestamp: string;
}

/** API error response */
export interface ApiError {
  success: false;
  error: {
    code: string;
    message: string;
    details?: Record<string, unknown>;
  };
  timestamp: string;
}

/** Pagination parameters */
export interface PaginationParams {
  page: number;
  limit: number;
}

/** Paginated response */
export interface PaginatedResponse<T> extends ApiResponse<T[]> {
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

/** API endpoint definitions */
export const API_ENDPOINTS = {
  // Auth
  AUTH_LOGIN: '/api/v1/auth/identify',
  AUTH_REGISTER: '/api/v1/auth/register',
  AUTH_REFRESH: '/api/v1/auth/refresh',
  AUTH_LOGOUT: '/api/v1/auth/logout',
  AUTH_VERIFY: '/api/v1/auth/verify-keystroke',
  AUTH_STEP_UP: '/api/v1/auth/step-up',

  // Enrollment
  ENROLLMENT_START: '/api/v1/enroll/start',
  ENROLLMENT_SESSION: '/api/v1/enroll/session',
  ENROLLMENT_SUBMIT: '/api/v1/enroll/session',
  ENROLLMENT_STATUS: '/api/v1/enroll/status',
  ENROLLMENT_FINALIZE: '/api/v1/enroll/finalize',

  // Profile
  PROFILE_ME: '/api/v1/profile/me',
  PROFILE_HEALTH: '/api/v1/profile/health',
  PROFILE_HISTORY: '/api/v1/profile/history',
  PROFILE_RHYTHM_PRINT: '/api/v1/profile/rhythm-print',
  PROFILE_UPDATE: '/api/v1/profile',
  PROFILE_DEVICES: '/api/v1/devices',

  // Settings
  SETTINGS_GET: '/api/v1/settings',
  SETTINGS_UPDATE: '/api/v1/settings',

  // Continuous Auth
  WS_CONTINUOUS_AUTH: '/ws',

  // Activity
  ACTIVITY_RECENT: '/api/v1/activity/recent',
  ACTIVITY_METRICS: '/api/v1/activity/metrics',
} as const;

/** Request for login — no email needed, identification by keystroke only */
export interface LoginRequest {
  features: import('./keystroke').BiometricFeatures;
  deviceFingerprint?: string;
}

/** Request for enrollment session submission */
export interface EnrollmentSubmitRequest {
  sessionId: string;
  events: import('./keystroke').KeystrokeEvent[];
  features: import('./keystroke').BiometricFeatures;
  typedText: string;
  promptId: string;
}

/** Request for step-up verification */
export interface StepUpRequest {
  method: import('./auth').StepUpMethod;
  payload: string;
}

/** Activity entry */
export interface ActivityEntry {
  id: string;
  type: 'login' | 'enrollment' | 'verification' | 'settings_change';
  status: 'success' | 'failure' | 'warning';
  score?: number;
  device: string;
  ip: string;
  timestamp: string;
  details?: string;
}

/** Profile metrics */
export interface ProfileMetrics {
  avgScore: number;
  totalLogins: number;
  successRate: number;
  avgWpm: number;
  avgAccuracy: number;
  profileAge: number;
  lastUpdated: string;
}
